import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Calendar, CheckCircle2, Circle, ChevronDown, Edit3, Zap, Eye, Send, Power, Mail, Sparkles, Tag, Users, ShoppingBag } from "lucide-react";

const VAR_HINT = (
  <div className="text-[11px] text-foreground/70 leading-relaxed bg-white/50 border border-border/50 rounded-md px-3 py-2">
    <strong className="text-red-wine">Smart variables — auto-injected:</strong>
    <span className="ml-1">{"{{first_name}}"}</span>{" · "}
    <span>{"{{member_name}}"}</span>{" · "}
    <span>{"{{affiliate_link}}"}</span>{" · "}
    <span>{"{{member_url}}"}</span>{" · "}
    <span>{"{{unsubscribe_url}}"}</span>
  </div>
);

function PreviewModal({ open, onClose, week, slot }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get(`/automation/rotation/preview/${week}/${slot}`);
        setData(r.data);
      } catch { toast.error("Could not load preview"); }
    })();
  }, [open, week, slot]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="rotation-preview-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-white">
          <div>
            <div className="text-[10px] tracking-wider-cap font-overline text-gold-deep">PREVIEW · WEEK {week} · SLOT {slot}</div>
            <h4 className="text-lg font-semibold text-red-wine mt-0.5">{data?.subject || "Loading..."}</h4>
          </div>
          <button onClick={onClose} className="text-foreground/70 hover:text-red-wine text-xl leading-none" data-testid="close-preview">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {data ? (
            <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: data.body }} />
          ) : <div className="text-center text-foreground/60 py-10">Loading…</div>}
        </div>
        <div className="px-5 py-3 border-t border-border bg-white text-[11px] text-foreground/70">
          This is rendered with YOUR affiliate code &amp; name. Each contact will see their own first name in the greeting.
        </div>
      </div>
    </div>
  );
}

function SlotCard({ s, onToggle, onEdit, onPreview, onSendTest, onRunNow, isOpen, editForm, setEditForm, onSave, onCancel, productKeys, segmentsMap }) {
  const slotColor = s.slot === "A" ? "#2E9F3A" : "#F39A1F";
  const slotLabel = s.slot === "A" ? "TUE" : "FRI";
  const pk = editForm.product_key || s.product_key || "";
  const pkLabel = pk ? (productKeys.find(p => p.key === pk)?.label || pk) : "";
  const excludedCount = pk ? (segmentsMap?.[pk] || 0) : 0;
  return (
    <div className="faith-card overflow-hidden" data-testid={`slot-${s.week}-${s.slot}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => onToggle(s)} className="shrink-0" data-testid={`toggle-${s.week}-${s.slot}`} title={s.enabled ? "Pause this email" : "Enable this email"}>
          {s.enabled ? <CheckCircle2 className="w-5 h-5 text-gold-deep"/> : <Circle className="w-5 h-5 text-foreground/40"/>}
        </button>
        <div className="w-12 h-12 rounded-md flex flex-col items-center justify-center shrink-0 text-white" style={{backgroundColor: slotColor, fontFamily:'Anton, sans-serif'}}>
          <span className="text-[10px] tracking-wider opacity-90">{slotLabel}</span>
          <span className="text-[14px] leading-none">W{String(s.week).padStart(2,'0')}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate text-foreground">{s.subject}</div>
          <div className="text-[10px] tracking-wider-cap text-foreground/70 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span>Slot {s.slot} · {s.enabled ? "Scheduled" : "Paused"}{s.is_custom && " · Edited"}</span>
            {s.product_key && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-red-wine/30 bg-red-wine/5 text-red-wine font-bold normal-case tracking-normal"
                    data-testid={`pitch-tag-${s.week}-${s.slot}`}
                    title="This slot pitches a product. Buyers of that product are excluded.">
                <Tag className="w-2.5 h-2.5"/>
                Pitches: {(productKeys.find(p => p.key === s.product_key)?.label || s.product_key).replace(/^.*·\s*/,'')}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => onPreview(s)} className="p-2 rounded-md hover:bg-white shrink-0" data-testid={`preview-${s.week}-${s.slot}`} title="Preview with your variables">
          <Eye className="w-4 h-4 text-foreground/70"/>
        </button>
        <button onClick={() => onSendTest(s)} className="p-2 rounded-md hover:bg-white shrink-0" data-testid={`send-test-${s.week}-${s.slot}`} title="Send a test to my own inbox">
          <Send className="w-4 h-4 text-foreground/70"/>
        </button>
        <button onClick={() => onEdit(s)} className="p-2 rounded-md hover:bg-white shrink-0" data-testid={`edit-${s.week}-${s.slot}`} title="Edit this email">
          <Edit3 className="w-4 h-4 text-red-wine"/>
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-border/60 bg-white p-4 space-y-3">
          {VAR_HINT}
          <input className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm" value={editForm.subject} onChange={e=>setEditForm({...editForm, subject:e.target.value})} data-testid={`subject-${s.week}-${s.slot}`} placeholder="Subject"/>
          <textarea rows={6} className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm font-mono" value={editForm.body} onChange={e=>setEditForm({...editForm, body:e.target.value})} data-testid={`body-${s.week}-${s.slot}`} placeholder="HTML body…"/>

          {/* BUYER/NON-BUYER SEGMENTATION DROPDOWN */}
          <div className="rounded-md border border-red-wine/20 bg-red-wine/5 p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wider-cap font-bold text-red-wine flex items-center gap-1.5">
              <ShoppingBag className="w-3.5 h-3.5"/>
              Buyer Segmentation (optional)
            </div>
            <p className="text-[11px] text-foreground/75 leading-relaxed">
              If this email pitches a product, pick it below. Contacts who already bought it will be <strong>silently skipped</strong> — no more pitching the book to people who already own it.
            </p>
            <select
              className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm"
              value={editForm.product_key || ""}
              onChange={e => setEditForm({...editForm, product_key: e.target.value || ""})}
              data-testid={`product-key-${s.week}-${s.slot}`}>
              <option value="">— No product · send to everyone —</option>
              {productKeys.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
            {pk && (
              <div className="text-[11px] text-foreground/80">
                <strong className="text-red-wine">{excludedCount}</strong> contact{excludedCount === 1 ? "" : "s"} already bought <em>{pkLabel}</em> — they will be excluded from this send.
              </div>
            )}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs font-overline uppercase tracking-wider-cap flex items-center gap-2">
              <input type="checkbox" checked={editForm.enabled} onChange={e=>setEditForm({...editForm, enabled:e.target.checked})} className="accent-[#2E9F3A]"/>
              Enabled
            </label>
            <div className="flex gap-2">
              <button onClick={() => onRunNow(s)} className="btn-outline-gold !py-1.5 !px-3 !text-[10px]" data-testid={`run-now-${s.week}-${s.slot}`} title="Send right now to my whole list">⚡ Send To My List Now</button>
              <button onClick={onCancel} className="btn-outline-gold !py-1.5 !px-3 !text-[10px]">Cancel</button>
              <button onClick={() => onSave(s)} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid={`save-${s.week}-${s.slot}`}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  BuyerSegments — compact card showing X buyers · Y non-buyers      */
/*  per product. Lets Mike see at a glance which slots will get       */
/*  filtered when they're tagged.                                     */
/* ------------------------------------------------------------------ */
function BuyerSegments({ segments, total }) {
  const significant = (segments || []).filter(s => s.buyers > 0);
  return (
    <div className="rounded-2xl border-2 border-red-wine/15 bg-white p-5 md:p-6" data-testid="buyer-segments-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-md bg-red-wine/10 flex items-center justify-center">
          <Users className="w-4 h-4 text-red-wine"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-wider-cap font-overline uppercase text-red-wine">Buyer vs Non-Buyer Segmentation</div>
          <h4 className="text-base font-semibold text-foreground">
            {total} contact{total === 1 ? "" : "s"} on your list ·{" "}
            {significant.length === 0 ? (
              <span className="text-foreground/70 italic">no buyers tagged yet</span>
            ) : (
              <span className="text-red-wine">{significant.length} product segment{significant.length === 1 ? "" : "s"} active</span>
            )}
          </h4>
        </div>
      </div>
      {significant.length === 0 ? (
        <p className="text-[12px] text-foreground/70 leading-relaxed">
          When somebody buys your book, course, or supplement we automatically tag them so future pitch emails skip them. Tag a buyer manually from the Contacts list, or it happens for you on every Stripe sale + every logged book sale.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {significant.map(s => (
            <div key={s.key} className="flex items-center justify-between px-3 py-2 rounded-md border border-border bg-white" data-testid={`segment-${s.key}`}>
              <div className="text-[12px] text-foreground/85 truncate pr-2">{s.label}</div>
              <div className="text-[11px] font-bold whitespace-nowrap">
                <span className="text-red-wine">{s.buyers}</span>
                <span className="text-foreground/50"> / {s.non_buyers + s.buyers}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailRotationEngine() {
  const [slots, setSlots] = useState([]);
  const [enabledCount, setEnabledCount] = useState(0);
  const [settings, setSettings] = useState({ auto_pilot: false, start_week: 1, started_at: null, last_sent: null });
  const [schedule, setSchedule] = useState({ slot_a_day: "Tuesday", slot_b_day: "Friday", send_hour_utc: 14 });
  const [openKey, setOpenKey] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", body: "", enabled: true, product_key: "" });
  const [preview, setPreview] = useState({ open: false, week: 1, slot: "A" });
  const [filter, setFilter] = useState("all"); // all | A | B | custom | tagged

  // NEW (buyer/non-buyer): product keys + segment counts for live exclusion math
  const [productKeys, setProductKeys] = useState([]);
  const [segments, setSegments] = useState([]);   // [{key,label,buyers,non_buyers}]
  const [totalContacts, setTotalContacts] = useState(0);
  const segmentsMap = useMemo(() => {
    const m = {};
    for (const s of segments) m[s.key] = s.buyers;
    return m;
  }, [segments]);

  const load = async () => {
    try {
      const r = await api.get("/automation/rotation");
      setSlots(r.data.slots);
      setEnabledCount(r.data.enabled);
      setSettings(r.data.settings);
      setSchedule(r.data.schedule);
      setProductKeys(r.data.product_keys || []);
    } catch {
      toast.error("Could not load email rotation");
    }
  };
  const loadSegments = async () => {
    try {
      const r = await api.get("/automation/segments");
      setSegments(r.data.segments || []);
      setTotalContacts(r.data.total_contacts || 0);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); loadSegments(); }, []);

  const filteredSlots = useMemo(() => {
    if (filter === "A") return slots.filter(s => s.slot === "A");
    if (filter === "B") return slots.filter(s => s.slot === "B");
    if (filter === "custom") return slots.filter(s => s.is_custom);
    if (filter === "tagged") return slots.filter(s => !!s.product_key);
    return slots;
  }, [slots, filter]);

  const toggleAutoPilot = async () => {
    try {
      const next = !settings.auto_pilot;
      await api.post("/automation/rotation/settings", { auto_pilot: next, start_week: settings.start_week || 1 });
      toast.success(next ? "Auto-Pilot ON · 2 emails per week to your list" : "Auto-Pilot paused");
      load();
    } catch { toast.error("Could not save settings"); }
  };

  const setStartWeek = async (week) => {
    try {
      await api.post("/automation/rotation/settings", { auto_pilot: settings.auto_pilot, start_week: week });
      load();
    } catch { toast.error("Could not save"); }
  };

  const onEdit = (s) => {
    const k = `${s.week}-${s.slot}`;
    if (openKey === k) { setOpenKey(null); return; }
    setOpenKey(k);
    setEditForm({ subject: s.subject, body: s.body, enabled: s.enabled, product_key: s.product_key || "" });
  };

  const onSave = async (s) => {
    try {
      await api.post(`/automation/rotation/${s.week}/${s.slot}`, editForm);
      toast.success(`Week ${s.week} Slot ${s.slot} saved`);
      setOpenKey(null);
      load();
    } catch { toast.error("Could not save"); }
  };

  const onToggle = async (s) => {
    try {
      await api.post(`/automation/rotation/${s.week}/${s.slot}`, {
        subject: s.subject, body: s.body, enabled: !s.enabled,
        product_key: s.product_key || "",
      });
      load();
    } catch { toast.error("Could not toggle"); }
  };

  const onPreview = (s) => setPreview({ open: true, week: s.week, slot: s.slot });

  const onSendTest = async (s) => {
    try {
      const r = await api.post(`/automation/rotation/send-test/${s.week}/${s.slot}`);
      if (r.data.ok) toast.success(`Test sent to ${r.data.to}`);
      else toast.warning(`Saved — but SMTP isn't responding right now (preview env)`);
    } catch { toast.error("Could not send test"); }
  };

  const onRunNow = async (s) => {
    if (!confirm(`This will send Week ${s.week} Slot ${s.slot} to ALL of your contacts right now. Continue?`)) return;
    try {
      const r = await api.post(`/automation/rotation/run-now/${s.week}/${s.slot}`);
      const ex = r.data.excluded_buyers || 0;
      if (ex > 0) {
        toast.success(`Sent to ${r.data.sent} contacts · ${ex} buyer${ex === 1 ? "" : "s"} skipped (already bought)`);
      } else {
        toast.success(`Sent to ${r.data.sent} of ${r.data.total} contacts`);
      }
      load();
      loadSegments();
    } catch { toast.error("Could not send"); }
  };

  const startedDateStr = settings.started_at ? new Date(settings.started_at).toLocaleDateString() : null;

  return (
    <section data-testid="email-rotation-engine" className="space-y-5">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border-2" style={{ background: "#EAF6FC", borderColor: "#2E9F3A" }}>
        <div className="relative px-6 py-8 grid md:grid-cols-[auto,1fr,auto] items-center gap-5">
          <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center shrink-0" style={{borderColor:'#2E9F3A', background:'#FFFFFF'}}>
            <Mail className="w-7 h-7" strokeWidth={1.75} style={{ color: "#2E9F3A" }}/>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-overline uppercase tracking-[0.3em]" style={{ color: "#F39A1F" }}>104-Email Rotation Engine · Done For You</div>
            <h3 className="text-2xl md:text-3xl uppercase leading-tight mt-1" style={{ color: "#0F172A" }}>2 Emails A Week. All Year. On Auto-Pilot.</h3>
            <p className="text-sm mt-2 max-w-2xl leading-relaxed" style={{ color: "#0F172A" }}>
              Every <strong className="text-[#F39A1F]">{schedule.slot_a_day}</strong> a faith apologetics piece goes out. Every <strong className="text-[#F39A1F]">{schedule.slot_b_day}</strong> a wellness/growth nudge — with your affiliate link baked in.
              Edit any one. Pause any one. Or just turn it on and trust Him with the rest.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-2 min-w-[180px]">
            <button
              onClick={toggleAutoPilot}
              data-testid="toggle-auto-pilot"
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs transition shadow-lg ${settings.auto_pilot ? "bg-[#F39A1F] text-[#0F172A] hover:bg-[#F39A1F]/90" : "bg-white text-red-wine hover:bg-white/90"}`}>
              <Power className="w-4 h-4"/>
              {settings.auto_pilot ? "Auto-Pilot ON" : "Turn Auto-Pilot ON"}
            </button>
            <div className="text-center">
              <div className="text-3xl text-[#F39A1F]" style={{fontFamily:'Anton, sans-serif'}}>{enabledCount}<span className="text-white/40 text-xl">/104</span></div>
              <div className="text-[10px] tracking-wider-cap text-white/60 mt-0.5">Enabled</div>
            </div>
          </div>
        </div>
        {settings.auto_pilot && (
          <div className="relative px-6 pb-5 -mt-2">
            <div className="bg-[#F39A1F]/10 border border-[#F39A1F]/30 rounded-md px-4 py-2.5 flex items-center gap-3 flex-wrap">
              <Zap className="w-4 h-4 text-[#F39A1F] shrink-0"/>
              <div className="text-[12px] text-white/90 flex-1 min-w-0">
                Started {startedDateStr || "now"} · Currently sending starting Week {settings.start_week}.
                {settings.last_sent && (
                  <> Last fired: W{settings.last_sent.week} {settings.last_sent.slot} on {new Date(settings.last_sent.at).toLocaleString()}.</>
                )}
              </div>
              <select value={settings.start_week} onChange={e => setStartWeek(parseInt(e.target.value))} className="bg-onyx text-white border border-[#F39A1F]/40 rounded px-2 py-1 text-[11px]" data-testid="start-week-select">
                {Array.from({ length: 52 }, (_, i) => i + 1).map(w => <option key={w} value={w}>Start: Week {w}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* BUYER vs NON-BUYER SEGMENTS (NEW) */}
      <BuyerSegments segments={segments} total={totalContacts}/>

      {/* FILTER PILLS */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] tracking-wider-cap text-foreground/70 mr-1">Show:</span>
        {[
          ["all", "All 104"],
          ["A", `${schedule.slot_a_day} · Faith (52)`],
          ["B", `${schedule.slot_b_day} · Growth (52)`],
          ["custom", "Edited Only"],
          ["tagged", "Pitching A Product"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider-cap border transition ${filter === k ? "bg-red-wine text-white border-red-wine" : "bg-white text-foreground border-border hover:border-red-wine/50"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* SLOTS */}
      <div className="grid md:grid-cols-2 gap-3" data-testid="rotation-grid">
        {filteredSlots.map(s => {
          const k = `${s.week}-${s.slot}`;
          return (
            <SlotCard
              key={k} s={s}
              onToggle={onToggle} onEdit={onEdit} onPreview={onPreview} onSendTest={onSendTest} onRunNow={onRunNow}
              isOpen={openKey === k}
              editForm={editForm} setEditForm={setEditForm}
              onSave={onSave} onCancel={() => setOpenKey(null)}
              productKeys={productKeys} segmentsMap={segmentsMap}
            />
          );
        })}
      </div>

      <PreviewModal open={preview.open} onClose={() => setPreview({ ...preview, open: false })} week={preview.week} slot={preview.slot}/>
    </section>
  );
}
